import React from 'react';
import { MessageCircle, Loader2, Heart } from 'lucide-react';
import { Trans } from 'react-i18next';
import Avatar from '../Avatar';

// Phase F4 — presence LED: SAME semantics as PersonCard (🟢 ≤5min, ⚪ offline).
export const PresenceLed = ({ online, username, prefix = 'msg' }) => (
  <span
    className={`absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-card ${
      online ? 'bg-emerald-500' : 'bg-neutral-600'
    }`}
    data-testid={`${prefix}-presence-${username}-${online ? 'online' : 'offline'}`}
    aria-hidden="true"
  />
);

// Phase F4 — SAME Voce Pro concept/badge as the PersonCard (is_vip_creator).
export const VoceProBadge = ({ username, label }) => (
  <span
    className="rounded border border-amber-500/40 bg-amber-500/10 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider gold-text"
    data-testid={`msg-badge-vocepro-${username}`}
  >
    {label}
  </span>
);

export const timeLabel = (iso, locale, yesterdayLabel) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const now = new Date();
  const startToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startYesterday = new Date(startToday);
  startYesterday.setDate(startYesterday.getDate() - 1);
  if (d >= startToday) return d.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
  if (d >= startYesterday) return yesterdayLabel;
  const weekAgo = new Date(startToday);
  weekAgo.setDate(weekAgo.getDate() - 6);
  if (d >= weekAgo) {
    const wd = d.toLocaleDateString(locale, { weekday: 'long' });
    return wd.charAt(0).toUpperCase() + wd.slice(1);
  }
  return d.toLocaleDateString(locale);
};

/**
 * ContactsPane — Phase F4 Voice-First messaging home.
 * A. Conversations, ordered by last message (backend order).
 * B. ♡ Preferiti WITHOUT a conversation yet (no duplicates, no empty
 *    conversations created — the thread is born on the first message).
 */
const ContactsPane = ({ conversations, favorites, activeUsername, onSelect, loading, t, locale }) => (
  <>
    <header className="border-b border-border px-4 py-3">
      <h2 className="flex items-center gap-2 text-sm font-semibold">
        <MessageCircle className="h-4 w-4" /> {t('pane_title')}
      </h2>
    </header>
    <div className="flex-1 overflow-y-auto" data-testid="contacts-list">
      {loading ? (
        <div className="flex justify-center p-6"><Loader2 className="h-5 w-5 animate-spin" /></div>
      ) : (
        <>
          {conversations.length === 0 && favorites.length === 0 && (
            <div className="px-4 py-10 text-center text-xs text-muted-foreground">
              <Trans i18nKey="empty_conversations" ns="messages" components={{ strong: <span className="font-semibold" /> }} />
            </div>
          )}

          {conversations.map((c) => (
            <button
              key={c.id}
              onClick={() => onSelect(c.username)}
              className={`flex w-full items-center gap-3 border-b border-border/60 px-3 py-2.5 text-left hover:bg-secondary ${
                activeUsername === c.username ? 'bg-secondary' : ''
              }`}
              data-testid={`conv-row-${c.username}`}
            >
              <div className="relative shrink-0">
                <Avatar src={c.avatar_url} name={c.username} size={40} />
                <PresenceLed online={c.is_online} username={c.username} prefix="conv" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-2">
                  <span className="flex min-w-0 items-center gap-1.5">
                    <p className="truncate text-sm font-semibold">@{c.username}</p>
                    {c.is_voce_pro && <VoceProBadge username={c.username} label={t('voce_pro')} />}
                  </span>
                  <span className="flex shrink-0 items-center gap-1.5">
                    <span className="text-[10px] text-muted-foreground" data-testid={`conv-time-${c.username}`}>
                      {timeLabel(c.last_at, locale, t('time_yesterday'))}
                    </span>
                    {c.unread_count > 0 && (
                      <span
                        className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-amber-500 px-1.5 text-[10px] font-bold text-black"
                        data-testid={`conv-unread-${c.username}`}
                      >
                        {c.unread_count}
                      </span>
                    )}
                  </span>
                </div>
                <p className="mt-0.5 line-clamp-1 text-[12px] text-muted-foreground">
                  {c.last_message || t('no_messages_yet')}
                </p>
              </div>
            </button>
          ))}

          {favorites.length > 0 && (
            <>
              <p
                className="flex items-center gap-1.5 px-4 pb-1 pt-4 text-[10.5px] font-bold uppercase tracking-wider text-muted-foreground"
                data-testid="favorites-section-title"
              >
                <Heart className="h-3 w-3" /> {t('favorites_title')}
              </p>
              {favorites.map((f) => (
                <button
                  key={f.user_id}
                  onClick={() => onSelect(f.username)}
                  className={`flex w-full items-center gap-3 border-b border-border/60 px-3 py-2.5 text-left hover:bg-secondary ${
                    activeUsername === f.username ? 'bg-secondary' : ''
                  }`}
                  data-testid={`fav-row-${f.username}`}
                >
                  <div className="relative shrink-0">
                    <Avatar src={f.avatar_url} name={f.username} size={40} />
                    <PresenceLed online={f.is_online} username={f.username} prefix="fav" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <span className="flex min-w-0 items-center gap-1.5">
                      <p className="truncate text-sm font-semibold">@{f.username}</p>
                      {f.is_voce_pro && <VoceProBadge username={f.username} label={t('voce_pro')} />}
                    </span>
                    <p className="mt-0.5 text-[12px] text-muted-foreground">{t('start_conversation')}</p>
                  </div>
                </button>
              ))}
            </>
          )}
        </>
      )}
    </div>
  </>
);

export default ContactsPane;
